import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FiSearch, FiPlus, FiEdit2, FiTrash2, FiHome, FiTag,
  FiChevronLeft, FiChevronRight, FiTool, FiCheckCircle, FiClock, FiBarChart2,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { infrastructureAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';

const STATUSES = ['Good', 'Needs Repair', 'Damaged', 'Under Repair', 'Repaired', 'Replaced'];
const RESOLVE_STATUSES = ['Repaired', 'Replaced', 'Good'];

const statusStyles = {
  Good: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Needs Repair': 'bg-amber-50 text-amber-700 border-amber-200',
  Damaged: 'bg-red-50 text-red-600 border-red-200',
  'Under Repair': 'bg-sky-50 text-sky-700 border-sky-200',
  Repaired: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Replaced: 'bg-slate-100 text-slate-600 border-slate-200',
};

const emptyRecordForm = {
  category: '', itemName: '', quantity: 1, location: '', status: 'Good', date: '', remarks: '',
};
const emptyCategoryForm = { name: '', description: '' };
const emptyResolveForm = { status: 'Repaired', remarks: '' };

const inputClass =
  'w-full px-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700';
const labelClass =
  'block text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5';

const AdminInfrastructure = () => {
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = role === 'admin';
  const canEditRecords = role === 'admin' || role === 'store_management';
  const canManageCategories = role === 'admin' || role === 'store_management';

  const [activeTab, setActiveTab] = useState('records');
  const [records, setRecords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 15;

  // Record modal
  const [recordModalOpen, setRecordModalOpen] = useState(false);
  const [recordForm, setRecordForm] = useState(emptyRecordForm);
  const [editingRecord, setEditingRecord] = useState(null);
  const [savingRecord, setSavingRecord] = useState(false);

  // Category modal
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [editingCategory, setEditingCategory] = useState(null);
  const [savingCategory, setSavingCategory] = useState(false);

  // Repair workflow
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [resolveForm, setResolveForm] = useState(emptyResolveForm);
  const [resolvingRecord, setResolvingRecord] = useState(null);
  const [resolvingSaving, setResolvingSaving] = useState(false);
  const [historyRecord, setHistoryRecord] = useState(null);

  // Maintenance report
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [reportStatus, setReportStatus] = useState('');
  const [maintenanceReport, setMaintenanceReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await infrastructureAPI.getCategories();
      setCategories(res.data?.data?.categories || []);
    } catch {
      toast.error('Failed to load categories.');
    }
  }, []);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (categoryFilter) params.category = categoryFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await infrastructureAPI.getRecords(params);
      setRecords(res.data?.data?.records || []);
      setPage(1);
    } catch {
      toast.error('Failed to load infrastructure records.');
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, statusFilter]);

  const fetchMaintenanceReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const params = {};
      if (reportFrom) params.from = reportFrom;
      if (reportTo) params.to = reportTo;
      if (reportStatus) params.status = reportStatus;
      const res = await infrastructureAPI.getReport(params);
      setMaintenanceReport(res.data?.data);
    } catch {
      toast.error('Failed to load maintenance report.');
    } finally {
      setReportLoading(false);
    }
  }, [reportFrom, reportTo, reportStatus]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  useEffect(() => {
    const timer = setTimeout(fetchRecords, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [fetchRecords, search]);

  useEffect(() => {
    if (activeTab === 'reports') fetchMaintenanceReport();
  }, [activeTab, fetchMaintenanceReport]);

  // ── Records ──────────────────────────────────────────────────────

  const openCreateRecord = () => {
    if (categories.length === 0) {
      toast.info('No categories defined yet. The Management Head must create categories first.');
      return;
    }
    setEditingRecord(null);
    setRecordForm({ ...emptyRecordForm, category: categories[0]?._id || '' });
    setRecordModalOpen(true);
  };

  const openEditRecord = (record) => {
    setEditingRecord(record);
    setRecordForm({
      category: record.category?._id || '',
      itemName: record.itemName || '',
      quantity: record.quantity ?? 1,
      location: record.location || '',
      status: record.status || 'Good',
      date: record.date ? new Date(record.date).toISOString().slice(0, 10) : '',
      remarks: record.remarks || '',
    });
    setRecordModalOpen(true);
  };

  const handleRecordSubmit = async (e) => {
    e.preventDefault();
    setSavingRecord(true);
    try {
      const payload = { ...recordForm, quantity: Number(recordForm.quantity) || 0, date: recordForm.date || undefined };
      if (editingRecord) {
        await infrastructureAPI.updateRecord(editingRecord._id, payload);
        toast.success('Record updated.');
      } else {
        await infrastructureAPI.createRecord(payload);
        toast.success('Record added.');
      }
      setRecordModalOpen(false);
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save record.');
    } finally {
      setSavingRecord(false);
    }
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm('Delete this infrastructure record?')) return;
    try {
      await infrastructureAPI.deleteRecord(id);
      setRecords((prev) => prev.filter((r) => r._id !== id));
      toast.success('Record deleted.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete record.');
    }
  };

  // ── Repair Workflow (Admin only) ──────────────────────────────────

  const handleMarkForRepair = async (record) => {
    if (!window.confirm(`Mark "${record.itemName}" for repair?`)) return;
    try {
      const res = await infrastructureAPI.markForRepair(record._id, {});
      const updated = res.data?.data?.record;
      setRecords((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
      toast.success('Record marked for repair.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to mark record for repair.');
    }
  };

  const openResolveModal = (record) => {
    setResolvingRecord(record);
    setResolveForm(emptyResolveForm);
    setResolveModalOpen(true);
  };

  const handleResolveSubmit = async (e) => {
    e.preventDefault();
    setResolvingSaving(true);
    try {
      const res = await infrastructureAPI.resolveRepair(resolvingRecord._id, resolveForm);
      const updated = res.data?.data?.record;
      setRecords((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
      toast.success('Record status updated.');
      setResolveModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update record status.');
    } finally {
      setResolvingSaving(false);
    }
  };

  // ── Categories ───────────────────────────────────────────────────

  const openCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm(emptyCategoryForm);
    setCategoryModalOpen(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name || '', description: category.description || '' });
    setCategoryModalOpen(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setSavingCategory(true);
    try {
      if (editingCategory) {
        await infrastructureAPI.updateCategory(editingCategory._id, categoryForm);
        toast.success('Category updated.');
      } else {
        await infrastructureAPI.createCategory(categoryForm);
        toast.success('Category created.');
      }
      setCategoryModalOpen(false);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save category.');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category? Categories with records cannot be deleted.')) return;
    try {
      await infrastructureAPI.deleteCategory(id);
      setCategories((prev) => prev.filter((c) => c._id !== id));
      toast.success('Category deleted.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete category.');
    }
  };

  const totalPages = Math.ceil(records.length / perPage);
  const paginated = records.slice((page - 1) * perPage, page * perPage);

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#1e3a8a]">School Infrastructure</h2>
          <p className="text-slate-500 text-sm mt-1">
            Infrastructure records, repair workflow and maintenance reports
          </p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'records' && canEditRecords && (
            <button onClick={openCreateRecord}
              className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all"
              style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
              <FiPlus className="w-4 h-4" /> Add Record
            </button>
          )}
          {activeTab === 'categories' && canManageCategories && (
            <button onClick={openCreateCategory}
              className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all"
              style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
              <FiPlus className="w-4 h-4" /> Add Category
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button onClick={() => setActiveTab('records')}
          className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            activeTab === 'records'
              ? 'bg-[#1D4ED8] text-white shadow-md'
              : 'bg-white text-slate-500 border border-blue-100 hover:bg-blue-50'
          }`}>
          Records
        </button>
        <button onClick={() => setActiveTab('categories')}
          className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            activeTab === 'categories'
              ? 'bg-[#1D4ED8] text-white shadow-md'
              : 'bg-white text-slate-500 border border-blue-100 hover:bg-blue-50'
          }`}>
          Categories {canManageCategories ? '' : '(view only)'}
        </button>
        <button onClick={() => setActiveTab('reports')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            activeTab === 'reports'
              ? 'bg-[#1D4ED8] text-white shadow-md'
              : 'bg-white text-slate-500 border border-blue-100 hover:bg-blue-50'
          }`}>
          <FiBarChart2 className="w-4 h-4" /> Reports
        </button>
      </div>

      {activeTab === 'records' && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-2xl border border-blue-100 p-4 mb-6 flex gap-3 items-center flex-wrap shadow-sm">
            <div className="relative flex-1 min-w-[200px]">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input type="text" placeholder="Search by item or location..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700 placeholder-blue-200"
              />
            </div>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2.5 border border-blue-100 rounded-xl text-sm text-slate-600 focus:outline-none focus:border-blue-400">
              <option value="">All categories</option>
              {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 border border-blue-100 rounded-xl text-sm text-slate-600 focus:outline-none focus:border-blue-400">
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="text-sm text-slate-400 flex-shrink-0">
              {records.length} record{records.length !== 1 ? 's' : ''}
            </div>
          </div>

          {loading ? <LoadingSpinner /> : (
            <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-50/50 border-b border-blue-100">
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Item</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Category</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Qty</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Location</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Status</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Date</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((record, idx) => (
                      <motion.tr key={record._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.03 }}
                        className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors">
                        <td className="px-5 py-4">
                          <span className="font-semibold text-[#1e3a8a] block">{record.itemName}</span>
                          {record.remarks && <span className="text-slate-400 text-xs">{record.remarks}</span>}
                        </td>
                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded-full text-xs font-semibold">
                            <FiTag className="w-3 h-3" /> {record.category?.name || '—'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-slate-600">{record.quantity}</td>
                        <td className="px-5 py-4 hidden md:table-cell text-slate-500">{record.location || '—'}</td>
                        <td className="px-5 py-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${statusStyles[record.status] || ''}`}>
                            {record.status}
                          </span>
                        </td>
                        <td className="px-5 py-4 hidden sm:table-cell text-slate-400 text-xs whitespace-nowrap">
                          {formatDate(record.date)}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            {(record.maintenanceHistory || []).length > 0 && (
                              <button onClick={() => setHistoryRecord(record)}
                                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Maintenance History">
                                <FiClock className="w-4 h-4" />
                              </button>
                            )}
                            {isAdmin && ['Good', 'Needs Repair', 'Damaged'].includes(record.status) && (
                              <button onClick={() => handleMarkForRepair(record)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-semibold transition-colors">
                                <FiTool className="w-3.5 h-3.5" /> Mark for Repair
                              </button>
                            )}
                            {isAdmin && record.status === 'Under Repair' && (
                              <button onClick={() => openResolveModal(record)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-semibold transition-colors">
                                <FiCheckCircle className="w-3.5 h-3.5" /> Resolve
                              </button>
                            )}
                            {canEditRecords && (
                              <>
                                <button onClick={() => openEditRecord(record)}
                                  className="p-1.5 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                                  <FiEdit2 className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDeleteRecord(record._id)}
                                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {paginated.length === 0 && (
                <div className="text-center py-16">
                  <FiHome className="w-12 h-12 text-blue-200 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">No infrastructure records yet</p>
                  {canEditRecords && (
                    <p className="text-slate-400 text-sm mt-1">Click "Add Record" to file the first one.</p>
                  )}
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 p-4 border-t border-blue-50">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                    className="p-2 rounded-xl border border-blue-100 hover:bg-blue-50 disabled:opacity-40 transition-colors">
                    <FiChevronLeft className="w-4 h-4 text-slate-500" />
                  </button>
                  <span className="text-sm text-slate-500 px-3">Page {page} of {totalPages}</span>
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="p-2 rounded-xl border border-blue-100 hover:bg-blue-50 disabled:opacity-40 transition-colors">
                    <FiChevronRight className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'categories' && (
        <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50/50 border-b border-blue-100">
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Category</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Description</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Created By</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Created</th>
                  {canManageCategories && (
                    <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {categories.map((category, idx) => (
                  <motion.tr key={category._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.03 }}
                    className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0 border border-blue-100">
                          <FiTag className="w-4 h-4 text-blue-500" />
                        </div>
                        <span className="font-semibold text-[#1e3a8a]">{category.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-slate-500">{category.description || '—'}</td>
                    <td className="px-5 py-4 hidden sm:table-cell text-slate-400 text-xs">{category.createdBy?.name || '—'}</td>
                    <td className="px-5 py-4 hidden sm:table-cell text-slate-400 text-xs whitespace-nowrap">{formatDate(category.createdAt)}</td>
                    {canManageCategories && (
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => openEditCategory(category)}
                            className="p-1.5 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteCategory(category._id)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {categories.length === 0 && (
            <div className="text-center py-16">
              <FiTag className="w-12 h-12 text-blue-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No categories defined yet</p>
              <p className="text-slate-400 text-sm mt-1">
                {canManageCategories
                  ? 'Click "Add Category" to create categories like Electrical, Furniture, IT Equipment...'
                  : 'The Management Head will define categories like Electrical, Furniture, IT Equipment...'}
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'reports' && (
        <div>
          <div className="bg-white rounded-2xl border border-blue-100 p-4 mb-6 flex flex-wrap gap-4 items-end shadow-sm">
            <div>
              <label className={labelClass}>From</label>
              <input type="date" className={inputClass} value={reportFrom}
                onChange={(e) => setReportFrom(e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>To</label>
              <input type="date" className={inputClass} value={reportTo}
                onChange={(e) => setReportTo(e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select className={inputClass} value={reportStatus}
                onChange={(e) => setReportStatus(e.target.value)}>
                <option value="">Damaged / Repaired / Replaced</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <button onClick={fetchMaintenanceReport}
              className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all"
              style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
              <FiBarChart2 className="w-4 h-4" /> Generate Report
            </button>
          </div>

          {reportLoading ? <LoadingSpinner /> : maintenanceReport && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                {['Needs Repair', 'Damaged', 'Repaired', 'Replaced'].map((s) => (
                  <div key={s} className="bg-white rounded-2xl border border-blue-100 p-4 shadow-sm">
                    <p className="text-slate-400 text-xs uppercase tracking-wider">{s}</p>
                    <p className="text-2xl font-bold text-[#1e3a8a] mt-1">{maintenanceReport.counts[s] || 0}</p>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-blue-50/50 border-b border-blue-100">
                        <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Item</th>
                        <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Category</th>
                        <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Location</th>
                        <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Status</th>
                        <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {maintenanceReport.records.map((record) => (
                        <tr key={record._id} className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors">
                          <td className="px-5 py-4 font-semibold text-[#1e3a8a]">{record.itemName}</td>
                          <td className="px-5 py-4 text-slate-500">{record.category?.name || '—'}</td>
                          <td className="px-5 py-4 hidden md:table-cell text-slate-500">{record.location || '—'}</td>
                          <td className="px-5 py-4">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${statusStyles[record.status] || ''}`}>
                              {record.status}
                            </span>
                          </td>
                          <td className="px-5 py-4 hidden sm:table-cell text-slate-400 text-xs whitespace-nowrap">{formatDate(record.date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {maintenanceReport.records.length === 0 && (
                  <div className="text-center py-16">
                    <FiBarChart2 className="w-12 h-12 text-blue-200 mx-auto mb-3" />
                    <p className="text-slate-500 font-medium">No matching records in this range</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Record Modal */}
      <Modal isOpen={recordModalOpen} onClose={() => setRecordModalOpen(false)}
        title={editingRecord ? 'Edit Infrastructure Record' : 'Add Infrastructure Record'} size="lg">
        <form onSubmit={handleRecordSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Category *</label>
              <select className={inputClass} required value={recordForm.category}
                onChange={(e) => setRecordForm({ ...recordForm, category: e.target.value })}>
                <option value="" disabled>Select category</option>
                {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Item Name *</label>
              <input className={inputClass} required value={recordForm.itemName}
                onChange={(e) => setRecordForm({ ...recordForm, itemName: e.target.value })}
                placeholder="e.g. Ceiling Fan" />
            </div>
            <div>
              <label className={labelClass}>Quantity *</label>
              <input className={inputClass} type="number" min="0" required value={recordForm.quantity}
                onChange={(e) => setRecordForm({ ...recordForm, quantity: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Location</label>
              <input className={inputClass} value={recordForm.location}
                onChange={(e) => setRecordForm({ ...recordForm, location: e.target.value })}
                placeholder="e.g. Class 5 Room" />
            </div>
            <div>
              <label className={labelClass}>Status *</label>
              <select className={inputClass} value={recordForm.status}
                onChange={(e) => setRecordForm({ ...recordForm, status: e.target.value })}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Date</label>
              <input className={inputClass} type="date" value={recordForm.date}
                onChange={(e) => setRecordForm({ ...recordForm, date: e.target.value })} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Remarks</label>
            <textarea className={inputClass} rows={2} value={recordForm.remarks}
              onChange={(e) => setRecordForm({ ...recordForm, remarks: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setRecordModalOpen(false)}
              className="px-5 py-2.5 border border-blue-100 rounded-xl text-sm font-semibold text-slate-500 hover:bg-blue-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={savingRecord}
              className="px-5 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
              {savingRecord ? 'Saving...' : editingRecord ? 'Update Record' : 'Add Record'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Category Modal */}
      <Modal isOpen={categoryModalOpen} onClose={() => setCategoryModalOpen(false)}
        title={editingCategory ? 'Edit Category' : 'Add Category'} size="sm">
        <form onSubmit={handleCategorySubmit} className="space-y-4">
          <div>
            <label className={labelClass}>Category Name *</label>
            <input className={inputClass} required value={categoryForm.name}
              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
              placeholder="e.g. Electrical, Furniture, IT Equipment" />
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea className={inputClass} rows={2} value={categoryForm.description}
              onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setCategoryModalOpen(false)}
              className="px-5 py-2.5 border border-blue-100 rounded-xl text-sm font-semibold text-slate-500 hover:bg-blue-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={savingCategory}
              className="px-5 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
              {savingCategory ? 'Saving...' : editingCategory ? 'Update Category' : 'Create Category'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Resolve Repair Modal */}
      <Modal isOpen={resolveModalOpen} onClose={() => setResolveModalOpen(false)}
        title={resolvingRecord ? `Resolve — ${resolvingRecord.itemName}` : 'Resolve'} size="sm">
        <form onSubmit={handleResolveSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>New Status *</label>
            <select className={inputClass} value={resolveForm.status}
              onChange={(e) => setResolveForm({ ...resolveForm, status: e.target.value })}>
              {RESOLVE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Remarks</label>
            <textarea className={inputClass} rows={2} value={resolveForm.remarks}
              onChange={(e) => setResolveForm({ ...resolveForm, remarks: e.target.value })}
              placeholder="e.g. Fan rewired and reinstalled" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setResolveModalOpen(false)}
              className="px-5 py-2.5 border border-blue-100 rounded-xl text-sm font-semibold text-slate-500 hover:bg-blue-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={resolvingSaving}
              className="px-5 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
              {resolvingSaving ? 'Saving...' : 'Update Status'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Maintenance History Modal */}
      <Modal isOpen={!!historyRecord} onClose={() => setHistoryRecord(null)}
        title={historyRecord ? `${historyRecord.itemName} — Maintenance History` : ''} size="md">
        {historyRecord && (
          <div className="overflow-x-auto border border-blue-100 rounded-2xl">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50/50 border-b border-blue-100">
                  <th className="text-left px-4 py-3 text-slate-500 text-xs font-semibold uppercase tracking-wider">Action</th>
                  <th className="text-left px-4 py-3 text-slate-500 text-xs font-semibold uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-slate-500 text-xs font-semibold uppercase tracking-wider">Date</th>
                  <th className="text-left px-4 py-3 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">By</th>
                </tr>
              </thead>
              <tbody>
                {[...(historyRecord.maintenanceHistory || [])]
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((h, idx) => (
                    <tr key={h._id || idx} className="border-b border-blue-50">
                      <td className="px-4 py-3 text-slate-600">{h.action}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${statusStyles[h.status] || ''}`}>
                          {h.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{formatDate(h.date)}</td>
                      <td className="px-4 py-3 hidden sm:table-cell text-slate-400 text-xs">{h.actionBy?.name || '—'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {(historyRecord.maintenanceHistory || []).length === 0 && (
              <p className="text-center text-slate-400 text-sm py-8">No maintenance history yet.</p>
            )}
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default AdminInfrastructure;
